import React from 'react';
import { useNavigate } from 'react-router-dom';

import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Grid,
  InputAdornment,
  OutlinedInput,
  Skeleton,
  Typography,
  debounce,
} from '@mui/material';

import dayjs from 'dayjs';

import {
  Offer,
  OfferCategory,
  OfferRequirements,
  OfferStatus,
  VenueListOffersFilter,
} from '../../../../graphql/API';
import { useListOfferQuery } from '../../../../hooks/reactQuery/useOfferQuery';
import { useAppSelector } from '../../../../redux/hooks';
import Icons from '../../../../themes/icons';
import { getTimeInTimezoneInDayjs } from '../../../../utils/dateV2';
import { VENUE_MANUAL_TAGS } from '../../../../utils/venue-tags';
import Button from '../../../Button/Button';
import { OfferNotFound } from '../../../Dialogs/CreateInvite/components/OfferNotFound';
import { useCreateInvite } from '../../../Dialogs/CreateInvite/context/CreateInvite.context';
import InfiniteScroll from '../../../InfiniteScroll/InfiniteScroll';
import { AlphabetSelect } from '../components/AlphabetSelect';
import { OfferCard } from '../components/OfferCard';

export const Step1: React.FC<{ onBack?: () => void }> = ({ onBack }) => {
  const navigate = useNavigate();
  const { state, dispatch } = useCreateInvite();
  const venueState = useAppSelector((state) => state.venue.value);
  const isOnlineBrand = venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online);

  const [searchText, setSearchText] = React.useState('');
  const [debounceSearchText, setDebounceSearchText] = React.useState('');

  const [filters, setFilters] = React.useState<Partial<VenueListOffersFilter>>({
    sortBy: 'title',
    sortDirection: 'asc',
  });

  const { data, isLoading, isFetching, hasNextPage, fetchNextPage } = useListOfferQuery({
    status: OfferStatus.active,
    search: debounceSearchText,
    ...filters,
  });

  const searchDebounce = React.useMemo(
    () => debounce((searchText: string) => setDebounceSearchText(searchText), 500),
    [],
  );

  React.useEffect(() => {
    searchDebounce(searchText);
  }, [searchText, searchDebounce]);

  const handleTextOnChange = React.useCallback<React.ChangeEventHandler<HTMLTextAreaElement>>(
    (e) => setSearchText(e.target.value),
    [setSearchText],
  );

  const handleAlphabeticallyChange = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      const v = e.target.value || 'title-asc';
      const [sortBy, sortDirection] = v.split('-');
      setFilters((prev) => ({ ...prev, sortBy, sortDirection }));
    },
    [],
  );

  const offerSelectionHandle = React.useCallback(
    (offer: Offer) => {
      if (offer) {
        if (offer.category === OfferCategory.event) {
          const startDateTime = getTimeInTimezoneInDayjs(
            offer.startDate,
            offer.startTime,
            offer.timeZoneId,
          );
          const endDateTime = getTimeInTimezoneInDayjs(
            offer.startDate,
            offer.endTime,
            offer.timeZoneId,
          );

          dispatch({
            type: 'update',
            payload: {
              offer: offer,
              dates: [dayjs(offer.startDate)],
              startTime: startDateTime,
              endTime: endDateTime,
              tag: offer.tag || ''
            },
          });
        } else if (offer.category === OfferCategory.product) {
          dispatch({
            type: 'update',
            payload: {
              offer: offer,
              dates: undefined,
              tag: offer.tag || ''
            },
          });
        } else {
          dispatch({
            type: 'update',
            payload: {
              offer: offer,
              dates: undefined,
              tag: offer.tag || ''
            },
          });
        }
      }
    },
    [dispatch],
  );

  const createOfferHandle = React.useCallback(() => {
    navigate('/offers/create?type=collab');
  }, [navigate]);

  const offers = isOnlineBrand
    ? data.filter((offer) => offer.category === OfferCategory.product)
    : data.filter(
        (offer) => offer.private || (!offer.private && offer.category === OfferCategory.event),
      );

  return (
    <Box>
      <Box
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
        sx={{ borderBottom: '1px solid #F1F2F1', backgroundColor: '#FCFCFC', px: 5, py: 3 }}
      >
        <Box display={'flex'} alignItems={'center'} gap={1.5}>
          <Button
            startIcon={<Icons.BackIcon />}
            onClick={() => {
              if (onBack) onBack();
            }}
          />
          <Typography fontWeight={'500'} sx={{ fontFamily: 'Poppins', fontSize: '18px' }}>
            Select an {isOnlineBrand ? 'product' : 'offer'}
          </Typography>
        </Box>
        <Box display={'flex'} alignItems={'center'} gap={1.5}>
          <AlphabetSelect
            value={`${filters.sortBy}-${filters.sortDirection}`}
            onChange={handleAlphabeticallyChange}
          />
          <OutlinedInput
            id='search-offers'
            placeholder='Search an offer...'
            startAdornment={
              <InputAdornment position='start'>
                <SearchIcon />
              </InputAdornment>
            }
            value={searchText}
            onChange={handleTextOnChange}
            size='small'
            fullWidth
            sx={{ border: '1px solid #EFEEEE', minWidth: '380px' }}
          />
        </Box>
      </Box>
      <Box
        sx={{
          backgroundColor: '#fff',
          px: 5,
          py: 3,
          height: 'calc(100vh - 160px)',
          overflow: 'auto',
        }}
      >
        <Grid container spacing={3} rowSpacing={3}>
          <InfiniteScroll
            isLoading={isLoading}
            isFetching={isFetching}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
          >
            {isLoading || isFetching ? (
              [...Array(9)].map((_, i) => (
                <Grid item xs={4} key={`skeleton-${i}`}>
                  <Skeleton variant='rectangular' height={200} />
                </Grid>
              ))
            ) : (
              <>
                {offers.map((offer) => (
                  <Grid item xs={4} key={offer.title}>
                    <OfferCard
                      offer={offer}
                      selected={state.offer?.id === offer.id}
                      onSelection={() => {
                        offerSelectionHandle(offer);
                      }}
                    />
                  </Grid>
                ))}
              </>
            )}
          </InfiniteScroll>
          {!isLoading && !isFetching && offers.length === 0 && (
            <Grid item xs={4}>
              <OfferNotFound onCreateOffer={createOfferHandle} />
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
};
